import { findDeepDisplayValue } from "../../services/tripDisplay";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { usePlantContext } from "../../context/plantContext";
import { useUserPermissions, useFactoryDashboardData } from "../../services/hooks";
import { formatDate as formatDateWithTimezone } from "../../services/functions";
import theme, { buttonClasses } from "../utils/theme";
import GenericDownloadTable from "../utils/GenericDownloadTable";
import { useGetInsightsVehiclesMutation } from "../../api/vmsSlice";
import { GenericTable, tCell, DatePicker, Dialog } from "../utils";
import dayjs from "dayjs";
import "./NewDashboard.css";

const REFRESH_INTERVAL_MS = 90000;

const DEFAULT_HEADER = [
  { label: "Vehicle No.", name: "vehicleNumber" },
  { label: "Supplier", name: "supplier.supplierName" },
  { label: "Product Category", name: "productCategory.productCategory" },
  { label: "Product", name: "product.productName" },
  { label: "Trip ID", name: "trip_counter" },
  { label: "Driver Name", name: "driverName" },
  { label: "Driver Number", name: "driverNumber" },
  { label: "Trip Status", name: "trip_status" },
];

// Helper to format date range for API
const formatDateForApi = (date) => 
  dayjs(date).add(1, "day").toDate().toISOString();

// Generate table headers from factory data
const generateHeaders = (factoryData) => {
  if (!factoryData?.length || !factoryData[0]?.count) return [];
  
  const countKeys = Object.keys(factoryData[0].count);
  const headers = countKeys.map((key) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  );
  return ["Checkpoint Name", ...headers];
};

// Popup Modal Component
const InsightPopup = ({ isOpen, onClose, title, headers, rows, isLoading }) => {
    if (!isOpen) return null;
  // Handle escape key to close moda
  return (
    <Dialog
        open={isOpen}
        handleClose={onClose}
        title={title}
        size = "lg"
        noBtn
    >
        <div style={popupStyles.content}>
          <GenericTable
            header={headers}
            rows={rows}
            isLoading={isLoading}
            IsTableBodyScroll
          />
        </div>
    </Dialog>
  );
};

export default function FactoryDashboard() {
  const { userPermissions } = useUserPermissions();
  const { plantId, timezone } = usePlantContext();
  const formatDate = (date, format) => formatDateWithTimezone(date, format, timezone);
  
  const hasPermission = userPermissions?.factoryDashboard?.value;
  
  const {
    factoryData,
    isLoading: factoryStatsLoading,
    getFactoryDashboardData,
  } = useFactoryDashboardData({ plantId, enabled: hasPermission });
  
  const [insightVehiclesTrigger, { isLoading: insightLoading }] = useGetInsightsVehiclesMutation();

  const [checkpointStartDate, setCheckpointStartDate] = useState(null);
  const [checkpointEndDate, setCheckpointEndDate] = useState(null);
  const [insightCheckpointList, setInsightCheckpointList] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupTitle, setPopupTitle] = useState("Vehicle Details");

  // Memoize API payload construction
  const getApiPayload = useCallback((additionalParams = {}) => {
    const basePayload = { plantId, ...additionalParams };
    
    if (checkpointStartDate && checkpointEndDate) {
      return {
        ...basePayload,
        startDate: formatDateForApi(checkpointStartDate),
        endDate: formatDateForApi(checkpointEndDate),
      };
    }
    return basePayload;
  }, [plantId, checkpointStartDate, checkpointEndDate]);

  // Fetch data function
  const fetchData = useCallback(() => {
    if (!hasPermission) return;
    getFactoryDashboardData(getApiPayload());
  }, [hasPermission, getFactoryDashboardData, getApiPayload]);

  // Initial fetch and refetch on plantId change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh interval
  useEffect(() => {
    if (!hasPermission) return;
    
    const intervalId = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchData, hasPermission]);

  // Handle vehicle click - opens popup
  const handleVehicleClick = useCallback(async (event, data) => {
    // console.log("Navigating to vehicle:", data);
    if (data.count <= 0 || !insightVehiclesTrigger) return;

    const payload = getApiPayload({
      checkpointId: data.checkpointID,
      key: { [data.key]: [] },
    });

    // Format title from key (e.g., "totalVehicles" -> "Total Vehicles")
    const formattedTitle = data.key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
    
    setPopupTitle(`${formattedTitle} - Vehicles`);
    setInsightCheckpointList([]);
    setIsPopupOpen(true);

    try {
      const res = await insightVehiclesTrigger(payload).unwrap();
      
      if (res?.error) {
        console.error("API Error:", res.message);
        setIsPopupOpen(false);
        return;
      }

      const rows = res?.data?.map((item, index) => {
        const row = [tCell(index + 1)];
        
        for (const header of DEFAULT_HEADER) {
          const value = findDeepDisplayValue(item, header.name.split("."));
          
          if (header.name === "vehicleNumber") {
            row.push(tCell(value, theme.themeOrange, "pointer"));
          } else if (header.type === "date") {
            row.push(tCell(formatDate(value)));
          } else {
            row.push(tCell(value));
          }
        }
        return row;
      }) || [];

      setInsightCheckpointList(rows);
    } catch (err) {
      console.error("Error fetching insight vehicles:", err);
      setIsPopupOpen(false);
    }
  }, [getApiPayload, insightVehiclesTrigger]);

  // Memoize processed data
  const factoryDataHeader = useMemo(
    () => generateHeaders(factoryData),
    [factoryData]
  );

  const factoryDataRows = useMemo(() => {
    if (!factoryData) return [];

    return factoryData.map((item) => {
      const row = [tCell(item.displayName, theme.themeOrange)];
      
      if (item.count) {
        Object.entries(item.count).forEach(([key, value]) => {
          row.push(
            tCell(
              value || 0,
              buttonClasses.rowview,
              "pointer",
              handleVehicleClick,
              { checkpointID: item.id, key, count: value || 0 }
            )
          );
        });
      }
      return row;
    });
  }, [factoryData, handleVehicleClick]);

  // Filter change handler
  const handleFilterChange = useCallback((e, name) => {
    const value = e?.$d ? new Date(e.$d).toISOString() : null;
    
    if (name === "checkpointStart") {
      setCheckpointStartDate(value);
    } else if (name === "checkpointEnd") {
      setCheckpointEndDate(value);
    }
  }, []);

  // Close popup handler
  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
    setInsightCheckpointList([]);
  }, []);

  // Insight table headers
  const insightTableHeaders = useMemo(
    () => ["S.No", ...DEFAULT_HEADER.map((h) => h.label)],
    []
  );

  // Early return if no permission
  if (!hasPermission) {
    return null;
  }

  return (
    <div className="chart-parent-container" style={{ margin: "2rem 0", padding: "1rem" }}>
      <div style={styles.headerContainer}>
        <div className="pie-chart-date-filter">
          <h5 style={{ marginTop: "8px", color:"var(--text-color)" }}>Factory Dashboard Data</h5>
          
          <div className="datepicker-container" style={styles.datePicker}>
            <DatePicker
              name="start"
              maxDATE={dayjs()}
              value={checkpointStartDate}
              required
              onChange={(val) => handleFilterChange(val, "checkpointStart")}
              label="Start Date"
            />
          </div>

          <div className="datepicker-container" style={styles.datePicker}>
            <DatePicker
              name="end"
              minDATE={checkpointStartDate ? dayjs(checkpointStartDate) : null}
              maxDATE={dayjs()}
              value={checkpointEndDate}
              required
              onChange={(val) => handleFilterChange(val, "checkpointEnd")}
              label="End Date"
            />
          </div>
        </div>

        <GenericDownloadTable
          headers={factoryDataHeader}
          rows={factoryDataRows}
          fileName="factory_dashboard"
        />
      </div>

      <div className="table-chart-container">
        <div className="chart-container">
          <GenericTable
            header={factoryDataHeader}
            rows={factoryDataRows}
            isLoading={factoryStatsLoading}
            isSerialNumber={false}
            IsTableBodyScroll
          />
        </div>
      </div>

      {/* Insight Popup Modal */}
      <InsightPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        title={popupTitle}
        headers={insightTableHeaders}
        rows={insightCheckpointList}
        isLoading={insightLoading}
      />
    </div>
  );
}

// Main component styles
const styles = {
  headerContainer: {
    display: "flex",
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "6px 0",
  },
  datePicker: {
    width: "180px",
    marginLeft: "12px",
  },
};

// Popup styles
const popupStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "90%",
    maxWidth: "1200px",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e0e0e0",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
  },
  closeButton: {
    ...buttonClasses.lynkitOrangeFill,
    height: "32px",
    minWidth: "32px",
    width: "32px",
    borderRadius: "50%",
    fontSize: "20px",
    lineHeight: "1",
    padding: 0,
  },
  content: {
    padding: "20px",
    overflow: "auto",
    flex: 1,
  },
};
